import { Query, QueryClient, QueryKey, hashKey, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  CourseDetailEntity, CourseDetailResponse, CourseEntity, CoursePayload, CourseResponse, CourseRouteEntity, CourseRouteResponse, RoutePayload,
  deserializeCourse, deserializeCourseDetail, deserializeRoute,
} from 'src/entities/Course.entity';

import { COURSES_BASE_PATH, COURSES_QUERY_KEY, courseQueryKey, coursesQueryKey } from './useCourses.query';
import { TIMETABLES_QUERY_KEY } from './useTimetables.query';

/**
 * コースの変更はコース一覧・自分以外の詳細（紐付けで相手の linked_trips や 3 列が変わる）・ダイヤ詳細（所属・地点名）に出るので、
 * courses と timetables のキャッシュをまとめて捨てる。useTimetablesQuery は refetchOnMount: false だが invalidate では取り直す。
 * exceptKey は削除したものの詳細（取り直すと 404）
 */
export const invalidateCourseMaster = (queryClient: QueryClient, exceptKey?: QueryKey) => {
  const predicate = exceptKey ? (q: Query) => q.queryHash !== hashKey(exceptKey) : undefined;
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEY], predicate }),
    queryClient.invalidateQueries({ queryKey: [TIMETABLES_QUERY_KEY], predicate }),
    // コピー元の候補はルート・所属が変わると変わる
    queryClient.invalidateQueries({ queryKey: ['routeSources'], predicate }),
  ]);
};

// 同じコースへの保存（基本情報・ルート・適用終了）には mutationKey ['courseSave'] を付け、useIsMutating の predicate で variables の id / courseId を見る（判断事項 16）

const setDetail = (queryClient: QueryClient, detail: CourseDetailEntity) => queryClient.setQueryData(courseQueryKey(detail.id), detail);

export const useCreateCourseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<CourseDetailEntity, Error, CoursePayload>({
    mutationFn: async (data) => deserializeCourseDetail((await axios.post<CourseDetailResponse>(COURSES_BASE_PATH, { data })).data),
    onSuccess: (detail) => { setDetail(queryClient, detail); return invalidateCourseMaster(queryClient); },
  });
};

export const useUpdateCourseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<CourseDetailEntity, Error, { id: number; data: CoursePayload }>({
    mutationKey: ['courseSave'],
    mutationFn: async ({ id, data }) => deserializeCourseDetail((await axios.patch<CourseDetailResponse>(`${COURSES_BASE_PATH}/${id}`, { data })).data),
    onSuccess: (detail) => { setDetail(queryClient, detail); return invalidateCourseMaster(queryClient); },
  });
};

export const useDeleteCourseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: number }>({
    mutationFn: async ({ id }) => { await axios.delete(`${COURSES_BASE_PATH}/${id}`); },
    // 消したコースの詳細は removeQueries も invalidate もしない。表示中だと取り直して 404 になり、詳細が読み込み中に戻って
    // 削除ダイアログごとアンマウントされ、mutate の onSuccess（選択を外す）が呼ばれなくなる。画面が選択を外せば gcTime で消える
    onSuccess: (_result, { id }) => invalidateCourseMaster(queryClient, courseQueryKey(id)),
  });
};

/** 一覧の並び替え。応答が並び替え後の一覧なので取り直さず置き換える（ドラッグ直後に順が戻って見えないように） */
export const useSortCoursesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<CourseEntity[], Error, { ids: number[] }>({
    mutationFn: async ({ ids }) => (await axios.put<CourseResponse[]>(`${COURSES_BASE_PATH}/sort_orders`, { data: { ids } })).data.map(deserializeCourse),
    onSuccess: (list) => { queryClient.setQueryData(coursesQueryKey(), list); },
  });
};

/** ルート（積地・地点）の保存。routeId が null は未所属の並びの作成（POST）。詳細キャッシュの routes だけ差し替えてから捨てる */
export const useSaveRouteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<CourseRouteEntity, Error, { courseId: number; routeId: number | null; data: RoutePayload }>({
    mutationKey: ['courseSave'],
    mutationFn: async ({ courseId, routeId, data }) => {
      const base = `${COURSES_BASE_PATH}/${courseId}/routes`;
      const res = routeId == null ? await axios.post<CourseRouteResponse>(base, { data }) : await axios.patch<CourseRouteResponse>(`${base}/${routeId}`, { data });
      return deserializeRoute(res.data);
    },
    onSuccess: (route, { courseId }) => {
      queryClient.setQueryData<CourseDetailEntity>(courseQueryKey(courseId), (prev) => prev && {
        ...prev,
        routes: prev.routes.some((r) => r.id === route.id) ? prev.routes.map((r) => (r.id === route.id ? route : r)) : [...prev.routes, route],
      });
      return invalidateCourseMaster(queryClient);
    },
  });
};

/** 適用終了日（routeId の並びだけ。log_web_issues#403 の 5）。endedOn null で解除。応答は CourseDetail */
export const useEndStopMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<CourseDetailEntity, Error, { courseId: number; routeId: number; placeId: number; endedOn: string | null }>({
    mutationKey: ['courseSave'],
    mutationFn: async ({ courseId, routeId, placeId, endedOn }) => deserializeCourseDetail(
      (await axios.put<CourseDetailResponse>(`${COURSES_BASE_PATH}/${courseId}/stop_ends`, {
        data: { route_id: routeId, place_id: placeId, ended_on: endedOn },
      })).data,
    ),
    onSuccess: (detail) => { setDetail(queryClient, detail); return invalidateCourseMaster(queryClient); },
  });
};
